import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Truck, Home, Briefcase, User, ArrowRight, Star, ChevronRight, Lock, Zap, CheckCircle2, Phone, Mail } from 'lucide-react';
import { SimonsCommercial } from './SimonsCommercial';
import { SimonsLandlord } from './SimonsLandlord';
import { SimonsTrucking } from './SimonsTrucking';
import { SimonsPersonal } from './SimonsPersonal';
import { SimonsPortal } from './SimonsPortal';
import { SimonsHeader, SimonsFooter } from '../components/SimonsNavigation';
interface SimonsAcquahAgencyProps {
  onBack?: () => void;
}
export function SimonsAcquahAgency({
  onBack
}: SimonsAcquahAgencyProps) {
  const [email, setEmail] = useState('');
  const [activePage, setActivePage] = useState<'home' | 'commercial' | 'landlord' | 'trucking' | 'personal' | 'portal'>('home');
  if (activePage === 'commercial') return <SimonsCommercial onBack={() => setActivePage('home')} setActivePage={setActivePage} data-id="element-1619" />;
  if (activePage === 'landlord') return <SimonsLandlord onBack={() => setActivePage('home')} setActivePage={setActivePage} data-id="element-1620" />;
  if (activePage === 'trucking') return <SimonsTrucking onBack={() => setActivePage('home')} setActivePage={setActivePage} data-id="element-1621" />;
  if (activePage === 'personal') return <SimonsPersonal onBack={() => setActivePage('home')} setActivePage={setActivePage} data-id="element-1622" />;
  if (activePage === 'portal') return <SimonsPortal onBack={() => setActivePage('home')} setActivePage={setActivePage} data-id="element-1623" />;
  const services = [{
    id: 'landlord',
    title: 'Landlord Insurance',
    icon: Home,
    desc: 'Protect your rental investments with comprehensive property coverage.'
  }, {
    id: 'trucking',
    title: 'Trucking & Commercial',
    icon: Truck,
    desc: 'Specialized liability and cargo insurance for the logistics industry.'
  }, {
    id: 'business',
    title: 'Small Business',
    icon: Briefcase,
    desc: 'Tailored BOP and professional liability for growing enterprises.'
  }, {
    id: 'personal',
    title: 'Personal Bundles',
    icon: User,
    desc: 'Save strategically by bundling home, auto, and life protection.'
  }];
  return <div className="min-h-screen bg-white text-[#002366] font-sans selection:bg-[#FFD700] selection:text-[#002366]" data-id="element-1624">
      <SimonsHeader activePage={activePage} setActivePage={setActivePage} data-id="element-1625" />

      {/* Hero Section */}
      <section className="relative pt-20 pb-32 px-6 overflow-hidden bg-gradient-to-b from-slate-50 to-white" data-id="element-1626">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1627">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-1628">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#FFD700]/10 border border-[#FFD700]/30 rounded-full text-[#002366] text-[10px] font-black uppercase tracking-[0.2em] mb-8" data-id="element-1629">
              <Star size={12} className="text-[#FFD700]" fill="currentColor" data-id="element-1630" />
              Elite InsurTech Platform
            </div>
            <h1 className="text-5xl lg:text-7xl font-black leading-[1.1] mb-8 tracking-tight" data-id="element-1631">
              Protect Smarter.
              <br data-id="element-1632" />
              <span className="text-[#DC143C]" data-id="element-1633">Save Strategically.</span>
            </h1>
            <p className="text-xl text-slate-600 max-w-xl mb-10 leading-relaxed font-medium" data-id="element-1634">
              Real expertise meets modern technology. We provide strategic
              insurance solutions for high-stakes industries and personal wealth
              protection.
            </p>
            <div className="flex flex-wrap gap-4" data-id="element-1635">
              <button className="px-8 py-4 bg-[#002366] text-white rounded-xl font-bold text-lg hover:bg-[#001a4d] transition-all shadow-xl shadow-[#002366]/20 flex items-center gap-2" data-id="element-1636">
                Start My Audit <ArrowRight size={20} data-id="element-1637" />
              </button>
              <div className="flex items-center gap-3 px-6 py-4 bg-white border border-slate-200 rounded-xl" data-id="element-1638">
                <div className="flex -space-x-2" data-id="element-1639">
                  {[1, 2, 3].map(i => <div key={i} className="w-8 h-8 rounded-full border-2 border-white bg-slate-200 overflow-hidden" data-id="element-1640">
                      <img src={`https://i.pravatar.cc/100?img=${i + 10}`} alt="User" data-id="element-1641" />
                    </div>)}
                </div>
                <div className="text-xs font-bold" data-id="element-1642">
                  <div className="flex text-[#FFD700] mb-0.5" data-id="element-1643">
                    {[1, 2, 3, 4, 5].map(i => <Star key={i} size={10} fill="currentColor" data-id="element-1644" />)}
                  </div>
                  <span className="text-slate-400" data-id="element-1645">4.8/5 from 2k+ Clients</span>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative" data-id="element-1646">
            <div className="bg-white rounded-[32px] shadow-[0_32px_64px_-12px_rgba(0,35,102,0.15)] border border-slate-100 p-8 relative z-10" data-id="element-1647">
              <h3 className="text-2xl font-black mb-6 flex items-center gap-3" data-id="element-1648">
                <Zap className="text-[#FFD700]" fill="currentColor" size={24} data-id="element-1649" />
                AI-Intake Engine
              </h3>
              <p className="text-sm text-slate-500 mb-8 font-medium" data-id="element-1650">
                Select your primary coverage area to begin our strategic risk
                assessment.
              </p>

              <div className="grid grid-cols-2 gap-4" data-id="element-1651">
                {services.map(s => <button key={s.id} onClick={() => setActivePage(s.id as any)} className="p-6 bg-slate-50 border border-slate-100 rounded-2xl hover:border-[#FFD700] hover:bg-white hover:shadow-xl transition-all group text-left" data-id="element-1652">
                    <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-[#002366] mb-4 shadow-sm group-hover:text-[#FFD700] transition-colors" data-id="element-1653">
                      <s.icon size={24} data-id="element-1654" />
                    </div>
                    <span className="text-sm font-black block mb-1" data-id="element-1655">
                      {s.title.split(' ')[0]}
                    </span>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-1656">
                      Explore →
                    </span>
                  </button>)}
              </div>

              <div className="mt-8 pt-8 border-t border-slate-100" data-id="element-1657">
                <div className="flex items-center justify-between text-xs font-bold text-slate-400 uppercase tracking-widest mb-4" data-id="element-1658">
                  <span data-id="element-1659">Carrier Partners</span>
                  <span className="text-[#002366]" data-id="element-1660">Live Rates</span>
                </div>
                <div className="flex justify-between items-center opacity-40 grayscale" data-id="element-1661">
                  <div className="h-6 w-20 bg-slate-400 rounded" data-id="element-1662"></div>
                  <div className="h-6 w-20 bg-slate-400 rounded" data-id="element-1663"></div>
                  <div className="h-6 w-20 bg-slate-400 rounded" data-id="element-1664"></div>
                </div>
              </div>
            </div>

            {/* Decorative Elements */}
            <div className="absolute -top-6 -right-6 w-24 h-24 bg-[#FFD700]/10 rounded-full blur-2xl" data-id="element-1665"></div>
            <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-[#DC143C]/5 rounded-full blur-3xl" data-id="element-1666"></div>
          </motion.div>
        </div>
      </section>

      {/* Service Bento Grid */}
      <section className="py-24 px-6 bg-[#002366]" data-id="element-1667">
        <div className="max-w-7xl mx-auto" data-id="element-1668">
          <div className="text-center mb-16" data-id="element-1669">
            <h2 className="text-4xl font-black mb-4 text-white" data-id="element-1670">
              Strategic Coverage Funnels
            </h2>
            <p className="text-[#FFD700] font-bold uppercase tracking-[0.2em] text-sm" data-id="element-1671">
              Elite Protection for Every Asset
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1672">
            {services.map((s, i) => <motion.div key={i} whileHover={{
            y: -8
          }} onClick={() => setActivePage(s.id as any)} className="p-8 bg-white/5 border border-white/10 rounded-[16px] shadow-sm hover:bg-white/10 transition-all group cursor-pointer" data-id="element-1673">
                <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center text-[#FFD700] mb-6 group-hover:bg-[#FFD700] group-hover:text-[#002366] transition-all" data-id="element-1674">
                  <s.icon size={28} data-id="element-1675" />
                </div>
                <h3 className="text-xl font-black mb-3 text-white" data-id="element-1676">
                  {s.title}
                </h3>
                <p className="text-sm text-white/60 leading-relaxed mb-6 font-medium" data-id="element-1677">
                  {s.desc}
                </p>
                <button className="text-xs font-black uppercase tracking-widest text-[#FFD700] flex items-center gap-2 group-hover:text-white transition-colors" data-id="element-1678">
                  Get Quote <ChevronRight size={14} data-id="element-1679" />
                </button>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section className="py-24 px-6 bg-slate-50" data-id="element-1680">
        <div className="max-w-7xl mx-auto" data-id="element-1681">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-1682">
            {[{
            title: 'Competitive Pricing',
            desc: 'We leverage our carrier relationships to secure the most strategic rates in the market.',
            icon: Zap
          }, {
            title: 'Problem-Solving Expertise',
            desc: 'Complex risks require strategic thinking. We specialize in hard-to-place commercial coverage.',
            icon: Shield
          }, {
            title: 'Fast Response Times',
            desc: 'In the insurance world, speed is security. Our team responds to all inquiries within 2 hours.',
            icon: Zap
          }].map((v, i) => <div key={i} className="flex flex-col items-center text-center" data-id="element-1683">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-[#FFD700] shadow-lg mb-8" data-id="element-1684">
                  <v.icon size={32} data-id="element-1685" />
                </div>
                <h3 className="text-2xl font-black mb-4" data-id="element-1686">{v.title}</h3>
                <p className="text-slate-500 font-medium leading-relaxed" data-id="element-1687">
                  {v.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Lead Magnet Section */}
      <section className="py-24 px-6" data-id="element-1688">
        <div className="max-w-5xl mx-auto bg-[#DC143C] rounded-[32px] p-12 md:p-20 text-white relative overflow-hidden shadow-2xl shadow-[#DC143C]/30" data-id="element-1689">
          <div className="relative z-10 text-center" data-id="element-1690">
            <h2 className="text-4xl md:text-6xl font-black mb-6 tracking-tight" data-id="element-1691">
              Get Your Free Insurance Review
            </h2>
            <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto font-medium" data-id="element-1692">
              Our strategic auditors will review your current policies and find
              hidden gaps or over-payments in your coverage.
            </p>

            <form className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto" data-id="element-1693">
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Enter your email address" className="flex-1 px-6 py-4 rounded-xl text-[#002366] font-bold focus:outline-none placeholder:text-slate-400 shadow-inner" data-id="element-1694" />
              <button className="px-8 py-4 bg-[#FFD700] text-[#002366] rounded-xl font-black uppercase tracking-widest hover:bg-white transition-all shadow-lg" data-id="element-1695">
                Start My Audit
              </button>
              {/* Hidden Source Tracking for CRM */}
              <input type="hidden" name="source" value="simons-acquah-lead-magnet" data-id="element-1696" />
            </form>
          </div>

          {/* Decorative Background */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 blur-[100px] rounded-full -mr-32 -mt-32" data-id="element-1697"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-black/10 blur-[100px] rounded-full -ml-32 -mb-32" data-id="element-1698"></div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="py-16 border-y border-slate-100 overflow-hidden" data-id="element-1699">
        <div className="max-w-7xl mx-auto px-6" data-id="element-1700">
          <div className="flex flex-wrap justify-center items-center gap-12 md:gap-24 opacity-30 grayscale hover:grayscale-0 transition-all duration-700" data-id="element-1701">
            <div className="h-8 w-32 bg-slate-400 rounded" data-id="element-1702"></div>
            <div className="h-8 w-32 bg-slate-400 rounded" data-id="element-1703"></div>
            <div className="h-8 w-32 bg-slate-400 rounded" data-id="element-1704"></div>
            <div className="h-8 w-32 bg-slate-400 rounded" data-id="element-1705"></div>
            <div className="h-8 w-32 bg-slate-400 rounded" data-id="element-1706"></div>
          </div>
        </div>
      </section>

      <SimonsFooter data-id="element-1707" />
    </div>;
}